/**
 * Centralized Axios Configuration
 * Handles authentication, request deduplication, and error handling
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
const API_KEY = process.env.REACT_APP_API_KEY || 'frac_d48567809b669980dc4fd133a106fee041784bac36b00dee6ab92a2326f9aad5';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY
  }
});

// Request deduplication cache
const pendingRequests = new Map();

// Generate unique key for request
const getRequestKey = (config) => {
  return `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
};

// Request interceptor - add auth and deduplication
axiosInstance.interceptors.request.use(
  (config) => {
    // Ensure API key is always present
    if (!config.headers['X-API-Key']) {
      config.headers['X-API-Key'] = API_KEY;
    }

    // Request deduplication - prevent duplicate simultaneous requests
    const requestKey = getRequestKey(config);
    
    if (pendingRequests.has(requestKey)) {
      // Return the existing promise instead of making a new request
      console.log('🔄 Deduplicating request:', config.method, config.url);
      return pendingRequests.get(requestKey);
    }

    // Store the request promise
    const controller = new AbortController();
    config.signal = controller.signal;
    
    const requestPromise = new Promise((resolve, reject) => {
      config._resolve = resolve;
      config._reject = reject;
    });
    
    pendingRequests.set(requestKey, { config, controller, promise: requestPromise });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors and cleanup
axiosInstance.interceptors.response.use(
  (response) => {
    // Remove from pending requests
    const requestKey = getRequestKey(response.config);
    pendingRequests.delete(requestKey);

    return response;
  },
  (error) => {
    const config = error.config;
    
    if (config) {
      const requestKey = getRequestKey(config);
      pendingRequests.delete(requestKey);
    }

    // Handle 401 errors - retry once with fresh auth
    if (error.response?.status === 401 && !config?._retry) {
      console.warn('⚠️ 401 Unauthorized - retrying with fresh auth headers');
      config._retry = true;
      config.headers['X-API-Key'] = API_KEY;
      return axiosInstance(config);
    }

    // Handle network errors
    if (!error.response) {
      console.error('❌ Network error:', error.message);
      error.userMessage = 'Network error. Please check your connection.';
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('❌ Server error:', error.response.status);
      error.userMessage = 'Server error. Please try again later.';
    }

    return Promise.reject(error);
  }
);

// Helper function to clear pending requests (useful for cleanup)
export const clearPendingRequests = () => {
  pendingRequests.forEach(({ controller }) => {
    controller.abort();
  });
  pendingRequests.clear();
};

// Export configured instance
export default axiosInstance;

// Export API_URL for backward compatibility
export { API_URL, API_KEY };
